import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTreeManagementPersistor } from '../persistence/PersistorContext';
import { CreateTreeInteractor, type CreateTreeViewState } from './CreateTreeInteractor';

interface CreateTreeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateTreeModal: React.FC<CreateTreeModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const persistor = useTreeManagementPersistor();
  
  const interactor = useMemo(() => {
    return new CreateTreeInteractor(persistor);
  }, [persistor]);

  const [viewState, setViewState] = useState<CreateTreeViewState>(() => interactor.getViewState());

  const updateViewState = useCallback(() => {
    setViewState(interactor.getViewState());
  }, [interactor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const createPromise = interactor.createTree();
    updateViewState();
    
    const success = await createPromise;
    updateViewState();
    
    if (success) {
      const currentViewState = interactor.getViewState();
      navigate(`/tree/${currentViewState.treeId}/edit`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Tree</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            disabled={viewState.isSubmitting}
          >
            ×
          </button>
        </div>
        
        <div className="modal-body">
          <p>Create a new skill tree:</p>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="treeName">Tree Name:</label>
              <input
                type="text"
                id="treeName"
                value={viewState.treeName}
                onChange={(e) => {
                  interactor.setTreeName(e.target.value);
                  updateViewState();
                }}
                placeholder="My Skill Tree"
                required
                disabled={viewState.isSubmitting}
                className={`form-input ${viewState.treeNameError ? 'form-input-error' : ''}`}
              />
              {viewState.treeNameError && (
                <div className="form-error">{viewState.treeNameError}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="treeId">Tree ID:</label>
              <input
                type="text"
                id="treeId"
                value={viewState.treeId}
                onChange={(e) => {
                  interactor.setTreeId(e.target.value);
                  updateViewState();
                }}
                placeholder="my-skill-tree"
                required
                disabled={viewState.isSubmitting}
                className={`form-input ${viewState.treeIdError ? 'form-input-error' : ''}`}
              />
              {viewState.treeIdError && (
                <div className="form-error">{viewState.treeIdError}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-checkbox-label">
                <input
                  type="checkbox"
                  checked={viewState.isPrivate}
                  onChange={(e) => {
                    interactor.setIsPrivate(e.target.checked);
                    updateViewState();
                  }}
                  disabled={viewState.isSubmitting}
                  className="form-checkbox"
                />
                <span>Private?</span>
              </label>
            </div>
            
            {viewState.error && (
              <div className="message error">
                {viewState.error}
              </div>
            )}
            
            <div className="form-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={viewState.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!viewState.isSubmitEnabled || viewState.isSubmitting}
              >
                {viewState.isSubmitting ? 'Creating ...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

